/**
 * Enhanced asset utility module to ensure images load correctly in all environments
 * including support for lovable-uploads and GitHub repo files
 */

// Cache of working asset URLs to avoid repeated testing
const workingAssetCache: Record<string, string> = {};

/**
 * Get the correct asset path for images stored in the public folder
 * Works in both development and production environments
 */
export const getAssetPath = (assetPath: string): string => {
  // If no asset path is provided, return an empty string
  if (!assetPath) {
    console.warn("[Asset Utils] No asset path provided");
    return "";
  }
  
  // Check if we have a cached working path for this asset
  if (workingAssetCache[assetPath]) {
    return workingAssetCache[assetPath];
  }

  // CRITICAL FIX FOR PRODUCTION: Use direct path with no leading slash
  // This is the most important change for production environments
  if (import.meta.env.PROD) {
    // The key fix is to use the asset name directly, with no leading slash
    // This ensures proper resolution from the root of the deployed site
    const fileName = assetPath.split('/').pop() as string;
    workingAssetCache[assetPath] = fileName; // Cache the path
    console.log(`[Asset Utils] Production path for ${assetPath}: ${fileName}`);
    return fileName;
  }

  // Special handling for lovable uploads
  if (assetPath.includes('lovable-uploads') || assetPath.match(/[a-f0-9-]{36}\.(png|jpg|jpeg|gif|webp)$/i)) {
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    workingAssetCache[assetPath] = normalizedPath;
    return normalizedPath;
  }
  
  // Special handling for GitHub repo files in specific directories
  if (assetPath.includes('new-images/') || assetPath.includes('testing123/')) {
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    workingAssetCache[assetPath] = normalizedPath;
    return normalizedPath;
  }

  // Handle files with spaces in names (common in the Disco project)
  if (assetPath.includes(' ') || assetPath.includes('-')) {
    // For development: add leading slash
    if (import.meta.env.DEV) {
      const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
      workingAssetCache[assetPath] = normalizedPath;
      return normalizedPath;
    }
    
    // For production: use filename directly
    const fileName = assetPath.split('/').pop() as string;
    workingAssetCache[assetPath] = fileName;
    return fileName;
  }

  // Default case for development - add leading slash
  if (import.meta.env.DEV) {
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    workingAssetCache[assetPath] = normalizedPath;
    return normalizedPath;
  }
  
  // Default case for any other environment - use direct filename
  const fileName = assetPath.split('/').pop() as string;
  workingAssetCache[assetPath] = fileName;
  return fileName;
};

/**
 * Debug asset loading and automatically find working paths
 * This function will try multiple strategies and cache the first working one
 */
export const debugAssetLoading = (assetPath: string): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!assetPath) {
      console.warn("[Asset Debug] No asset path provided");
      resolve(null);
      return;
    }
    
    // If we already have a working path cached, use it
    if (workingAssetCache[assetPath]) {
      console.log(`[Asset Debug] Using cached path: ${workingAssetCache[assetPath]}`);
      resolve(workingAssetCache[assetPath]);
      return;
    }
    
    const normalizedPath = assetPath.startsWith('/') ? assetPath.substring(1) : assetPath;
    const encodedPath = encodeURI(normalizedPath);
    
    console.log(`[Asset Debug] Testing paths for asset: ${assetPath}`);
    console.log(`[Asset Debug] Current hostname: ${window.location.hostname}`);
    
    // Define all possible paths to try, in order of preference
    const pathsToTry = [
      `/${encodedPath}`,
      `/assets/${encodedPath}`,
      `/images/${encodedPath}`,
      `/public/${encodedPath}`,
    ];
    
    // If it's likely a lovable upload, add those paths
    if (assetPath.includes('lovable-uploads') || assetPath.match(/[a-f0-9-]{36}\.(png|jpg|jpeg|gif|webp)$/i)) {
      // Extract just the filename if it's a path
      const filename = assetPath.split('/').pop() || assetPath;
      
      pathsToTry.unshift(
        `/lovable-uploads/${filename}`,
        `/public/lovable-uploads/${filename}`
      );
    }
    
    // Try to load the image with each path
    let loadedPath: string | null = null;
    let remainingPaths = pathsToTry.length;
    
    pathsToTry.forEach((path) => {
      const fullPath = window.location.origin + path;
      const img = new Image();
      
      img.onload = () => {
        if (!loadedPath) {
          console.log(`[Asset Debug] Path works: ${path}`);
          loadedPath = path;
          workingAssetCache[assetPath] = path;
          resolve(path);
        }
      };
      
      img.onerror = () => {
        remainingPaths--;
        if (remainingPaths === 0 && !loadedPath) {
          // All paths failed
          console.warn(`[Asset Debug] All paths failed for: ${assetPath}`);
          resolve(null);
        }
      };
      
      img.src = fullPath;
    });
  });
};

/**
 * Try multiple asset paths and use the first one that works
 * This is a more reliable way to load assets in production
 */
export const tryMultipleAssetPaths = (assetPath: string, callback: (successPath: string | null) => void): void => {
  // Check cache first
  if (workingAssetCache[assetPath]) {
    callback(workingAssetCache[assetPath]);
    return;
  }
  
  // Get the file name without the path
  const fileName = assetPath.split('/').pop() || assetPath;
  
  // Define asset paths to try based on the environment
  let paths: string[] = [];
  
  if (import.meta.env.PROD) {
    // In production, prioritize direct file name access at root level
    paths = [
      `${fileName}`, // Direct filename - MOST IMPORTANT FOR PRODUCTION
      `/${fileName}`, // With leading slash
      `/assets/${fileName}`, // In assets directory
      `${window.location.origin}/${fileName}` // Absolute URL
    ];
  } else {
    // In development, use standard path resolution
    paths = [
      `/${assetPath}`, // Standard path with leading slash
      `/${fileName}`, // Direct filename with leading slash  
      `/assets/${assetPath}`, // In assets directory
      `/public/${assetPath}`, // In public directory
      `${window.location.origin}/${assetPath}` // Absolute URL
    ];
  }
  
  // Add lovable-uploads specific paths if needed
  if (assetPath.includes('lovable-uploads') || assetPath.match(/[a-f0-9-]{36}\.(png|jpg|jpeg|gif|webp)$/i)) {
    const uploadFileName = fileName;
    paths = [
      `/lovable-uploads/${uploadFileName}`,
      `/public/lovable-uploads/${uploadFileName}`,
      ...paths
    ];
  }
  
  let successCount = 0;
  let failCount = 0;
  let successPath: string | null = null;
  
  // Try each path
  paths.forEach(path => {
    const img = new Image();
    img.onload = () => {
      successCount++;
      if (!successPath) {
        successPath = path;
        // Cache the working path for future use
        workingAssetCache[assetPath] = path;
        console.log(`✅ Found working path for ${assetPath}: ${path}`);
      }
      checkComplete();
    };
    img.onerror = () => {
      failCount++;
      checkComplete();
    };
    img.src = path;
  });
  
  function checkComplete() {
    if (successCount + failCount === paths.length) {
      callback(successPath);
    }
  }
};

/**
 * Enhanced getAssetPath that automatically tries multiple paths
 * and returns a promise that resolves to the working path
 */
export const getAssetPathAsync = async (assetPath: string): Promise<string> => {
  // Check cache first
  if (workingAssetCache[assetPath]) {
    return workingAssetCache[assetPath];
  }
  
  // Try to find a working path
  return new Promise((resolve) => {
    tryMultipleAssetPaths(assetPath, (workingPath) => {
      if (workingPath) {
        resolve(workingPath);
      } else {
        // Fallback to the standard path if nothing works
        const fallbackPath = `/${assetPath}`;
        resolve(fallbackPath);
      }
    });
  });
};

// Helper to get absolute asset URL
export const getAbsoluteAssetUrl = (assetPath: string): string => {
  const path = getAssetPath(assetPath);
  return `${window.location.origin}${path}`;
};

// Function to get production URL for assets
export const getProductionAssetUrl = (assetPath: string): string => {
  const normalizedPath = assetPath.startsWith('/') ? assetPath.substring(1) : assetPath;
  const encodedPath = encodeURI(normalizedPath);
  
  // Use the current origin to ensure assets are loaded from the same domain
  return `${window.location.origin}/${encodedPath}`;
};

/**
 * Handle uploaded files specifically (from lovable-uploads directory)
 */
export const getUploadedFilePath = (fileName: string): string => {
  // If it's already a full path with the directory, use it directly
  if (fileName.includes('lovable-uploads/')) {
    return `/${fileName}`;
  }
  
  // If it has a UUID pattern, assume it's an uploaded file
  if (fileName.match(/[a-f0-9-]{36}\.(png|jpg|jpeg|gif|webp)$/i)) {
    return `/lovable-uploads/${fileName}`;
  }
  
  // Otherwise, assume it's just the filename and add the directory
  return `/lovable-uploads/${fileName}`;
};

/**
 * Check if a file is likely an uploaded file based on its name or path
 */
export const isUploadedFile = (filePath: string): boolean => {
  return filePath.includes('lovable-uploads') || 
         filePath.match(/[a-f0-9-]{36}\.(png|jpg|jpeg|gif|webp)$/i) !== null;
};

/**
 * Scan for images in GitHub repository directories 
 * Returns a promise that resolves to an array of found image paths
 */
export const scanGitHubRepoImages = async (): Promise<string[]> => {
  const potentialDirectories = [
    '/new-images',
    '/public/new-images',
    '/testing123',
    '/assets/new-images'
  ];
  
  const foundImages: string[] = [];
  const potentialExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg'];
  
  // Try some common filenames in each directory
  const filenames = [
    'test-image-1',
    'test-image-2',
    'repo-image-1',
    'repo-image-2', 
    'image'
  ];
  
  for (const dir of potentialDirectories) {
    for (const file of filenames) {
      for (const ext of potentialExtensions) {
        const path = `${dir}/${file}${ext}`;
        const exists = await testFileExists(path);
        
        if (exists) {
          console.log(`✅ Found GitHub repo image: ${path}`);
          foundImages.push(path);
        }
      }
    }
  }
  
  return foundImages;
};

/**
 * Check if a path is likely a GitHub repo image (not a lovable upload)
 */
export const isGitHubRepoImage = (path: string): boolean => {
  return (
    path.includes('/new-images/') || 
    path.includes('/testing123/') || 
    path.includes('/uber-ubersonique')
  ) && !path.includes('lovable-uploads');
};

// Test if a file exists at a given path
export const testFileExists = (filePath: string): Promise<boolean> => {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = filePath;
  });
};
